import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getColor } from '../../theme/colors';
import { CONTEXTUAL_KPIS } from '../../config/architectureConfig';

const matchesQuery = (kpi, q) => {
  if (!q) return true;
  const hay = `${kpi.name} ${kpi.definition || kpi.description || ''} ${kpi.formula || ''} ${kpi.source || ''}`.toLowerCase();
  return hay.includes(q);
};

function KpiTable({ group, query }) {
  const color = getColor(group.colorName);
  const visible = group.kpis.filter((kpi) => matchesQuery(kpi, query));

  if (visible.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg bg-white p-6 text-center text-xs text-gray-500">
        No KPIs in <span className="font-medium text-gray-700">{group.domain}</span> match
        <span className="font-medium text-gray-700"> “{query}”</span>.
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <div className="border-b border-gray-100 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50">
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              <th className="px-4 py-2 w-[18%]">Name</th>
              <th className="px-4 py-2 w-[38%]">Definition</th>
              <th className="px-4 py-2 w-[28%]">Formula</th>
              <th className="px-4 py-2 w-[16%]">Source</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((kpi) => (
              <tr key={kpi.name} className="border-t border-gray-100 align-top hover:bg-gray-50/60">
                <td className="px-4 py-2.5">
                  <div className="flex items-start gap-2">
                    <div className={`mt-1.5 w-1.5 h-1.5 rounded-full ${color.bgSolid} flex-shrink-0`} />
                    <span className="text-sm font-medium text-gray-800">{kpi.name}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-gray-600 leading-relaxed">
                  {kpi.definition || kpi.description || '—'}
                </td>
                <td className="px-4 py-2.5">
                  {kpi.formula ? (
                    <code className="font-mono text-[11px] text-gray-700 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded inline-block">
                      {kpi.formula}
                    </code>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-gray-500">{kpi.source || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CollapsibleSearch({ query, onChange }) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  // Auto-focus the input the moment it appears.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Stay open while there's a query — closing would lose context.
  const handleBlur = () => {
    if (!query) setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onChange('');
      setOpen(false);
    }
  };

  const handleClear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="p-2 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
        aria-label="Search KPIs"
        title="Search KPIs"
      >
        <MagnifyingGlassIcon className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className="relative w-56">
      <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="Search KPIs…"
        className="w-full pl-9 pr-9 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
      />
      {query && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          aria-label="Clear search"
        >
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export default function ContextualKpisTab() {
  const defaultDomain = useMemo(() => {
    const finance = CONTEXTUAL_KPIS.find((g) => g.domain === 'Finance');
    return finance ? finance.domain : CONTEXTUAL_KPIS[0]?.domain;
  }, []);

  const [activeDomain, setActiveDomain] = useState(defaultDomain);
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const isSearching = q.length > 0;

  // Per-domain match counts for the tab badges.
  const matchCounts = useMemo(() => {
    const counts = {};
    CONTEXTUAL_KPIS.forEach((group) => {
      counts[group.domain] = q
        ? group.kpis.filter((kpi) => matchesQuery(kpi, q)).length
        : group.kpis.length;
    });
    return counts;
  }, [q]);

  const activeGroup = CONTEXTUAL_KPIS.find((g) => g.domain === activeDomain);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">Contextual KPIs</h3>
        <p className="text-xs text-gray-500">
          KPIs surfaced per business domain. Each metric is computed against the canonical model
          and contextualized to the operator viewing it. Pick a domain to see its KPIs.
        </p>
      </div>

      {/* Domain tabs + search (single row; tabs scroll horizontally if needed) */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 flex-nowrap overflow-x-auto min-w-0 flex-1">
          {CONTEXTUAL_KPIS.map((group) => {
            const color = getColor(group.colorName);
            const isActive = group.domain === activeDomain;
            const matchCount = matchCounts[group.domain];
            const dimmed = isSearching && matchCount === 0;
            return (
              <button
                key={group.domain}
                onClick={() => setActiveDomain(group.domain)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex-shrink-0 whitespace-nowrap ${
                  isActive
                    ? `${color.bgSolid} text-white border-transparent`
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                } ${dimmed ? 'opacity-40' : ''}`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    isActive ? 'bg-white/80' : color.bgSolid
                  }`}
                />
                {group.domain}
                <span
                  className={`ml-1 text-[10px] font-semibold ${
                    isActive ? 'text-white/80' : 'text-gray-400'
                  }`}
                >
                  {isSearching ? `${matchCount}/${group.kpis.length}` : group.kpis.length}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex-shrink-0">
          <CollapsibleSearch query={query} onChange={setQuery} />
        </div>
      </div>

      {activeGroup?.description && (
        <p className="text-xs text-gray-500">{activeGroup.description}</p>
      )}

      {activeGroup && <KpiTable group={activeGroup} query={q} />}
    </div>
  );
}
